import { domainSettingsUrl, userInfoUrl } from "./infrastructure/endpoints";
import { actionCreator, getDomainFromLocalStorage } from "./common.reducer";
import { actionCreatorAuth } from "../auth/auth.reducer";
import {
  IDomain,
  IDomainSettings,
  IGetLandlordResponse,
  IGetTenantResponse,
  isLandlordTypeGuard,
  ITenantAccessStatus,
} from "./models/userInfo";
import { get } from "./infrastructure/requests";
import { IDomainSetting, IFavorite } from "./types";
import { IApplicationState } from "redux/root-reducer";
import { isArray } from "lodash";

export const getUserInfo =
  (routeDomain: string | undefined, history: any) =>
  async (dispatch: ({}) => void) => {
    dispatch(actionCreator.fetching);
    try {
      const body = await get<IGetLandlordResponse | IGetTenantResponse>(
        userInfoUrl
      );
      if (!body.Domains) {
        dispatch(actionCreatorAuth.unauthorized);
        return;
      }
      let domain;
      const domainCode = localStorage.getItem("domainCode");
      if (domainCode && domainCode.length > 0) {
        domain = JSON.parse(domainCode).Code;
      }
      const selectedDomain = domain ?? body.Domains[0].Code ?? routeDomain;
      const settings = await getDomainSettings(selectedDomain, body);
      if (!settings) {
        dispatch(actionCreatorAuth.loginError);
        return;
      }
      if (!isLandlordTypeGuard(body)) {
        dispatch(
          actionCreator.fetchingUserInfoSuccess({
            Email: body.Email,
            PhoneNumbers: body.PhoneNumbers ?? [],
            Domains: body.Domains,
            DomainSettings: settings,
            OccupationDate: body.OccupationDate,
            NodeID: body.NodeID,
          })
        );
      } else {
        dispatch(
          actionCreator.fetchingUserInfoSuccess({
            GivenName: body.GivenName,
            SurName: body.SurName,
            Domains: body.Domains,
            DomainSettings: settings,
          })
        );
      }

      if (body.Domains.length >= 1) {
        let domain: IDomain | undefined = undefined;
        if (shouldUseDomainFromRoute(routeDomain, body.Domains)) {
          domain = body.Domains.find((d) => d.Code === routeDomain);
          if (domain) {
            dispatch(actionCreator.setSelectedDomain(domain));
          }
        } else if (localStorage.getItem("domainCode") !== null) {
          domain = JSON.parse(
            // @ts-ignore
            localStorage.getItem("domainCode")
          );
          if (domain) dispatch(actionCreator.setSelectedDomain(domain));
        } else {
          domain = body.Domains[0];
          dispatch(actionCreator.setSelectedDomain(domain));
        }
        if (!isLandlordTypeGuard(body)) {
          const path: string = history.location.pathname;
          if (path.endsWith("/"))
            history.push(`/${domain?.Code}/safetySensors/${body.NodeID}`);
        }
      }
    } catch (e) {
      dispatch(actionCreator.fetchingUserInfoError);
      throw new Error((e as Error).message);
    } finally {
      dispatch(actionCreator.userIsReady());
    }
  };

const shouldUseDomainFromRoute = (
  routeDomain: string | undefined,
  availableDomains: IDomain[]
) => {
  if (routeDomain) {
    return availableDomains.some((d) => d.Code === routeDomain);
  }

  return false;
};

export const setFavorites =
  (favorite: IFavorite) =>
  async (dispatch: ({}) => void, getState: () => IApplicationState) => {
    let favorites = getState().common.favorites;

    if (favorites.find((f) => f.node.ID === favorite.node.ID)) {
      favorites = favorites.filter((f) => f.node.ID !== favorite.node.ID);
    } else {
      if (isArray(favorites)) {
        favorites = [...favorites, favorite];
      } else {
        favorites = [favorite];
      }
    }

    localStorage.setItem("favorites", JSON.stringify(favorites));
    dispatch(actionCreator.setFavorites(favorites));
  };

export const setIncludeVAT =
  (includeVAT: boolean) => async (dispatch: ({}) => void) => {
    localStorage.setItem("includeVAT", includeVAT.toString());
    dispatch(actionCreator.setIncludeVAT(includeVAT));
  };

export const canUserViewTemperature = (
  settings: readonly IDomainSetting[]
): boolean =>
  settings.find((ds) => ds.Name === "TenantShowTemperature")?.Value === "True";

export const canUserViewTemperatureHours = (
  settings: readonly IDomainSetting[]
): boolean =>
  settings.find((ds) => ds.Name === "TenantShowHours")?.Value === "True";

export const canUserUseMobileApp = (
  settings: readonly IDomainSetting[]
): boolean => settings.find((ds) => ds.Name === "MobileApp")?.Value === "True";

export const canUserViewMeters = (
  settings: readonly IDomainSetting[]
): boolean =>
  settings.find((ds) => ds.Name === "SecurityApp")?.Value === "True";

export const canUserViewBillingResults = (
  settings: readonly IDomainSetting[]
): boolean =>
  settings.find((ds) => ds.Name === "TenantShowBillingResults")?.Value ===
  "True";

export const getCurrency = (settings: readonly IDomainSetting[]) => {
  return settings.find((ds) => ds.Name === "Currency")!.Value;
};

export const getTimeZone = (settings: readonly IDomainSetting[]) => {
  return settings.find((ds) => ds.Name === "TimeZoneIANA")!.Value;
};
export const getCulture = (settings: readonly IDomainSetting[]) => {
  return settings.find((ds) => ds.Name === "DefaultCulture")!.Value;
};

export const getDomain = (stateDomain: IDomain | null): string | undefined => {
  if (stateDomain) return stateDomain.Code;
  const domainFromLocalStorage = getDomainFromLocalStorage();
  if (domainFromLocalStorage) return domainFromLocalStorage.Code;
  const trimmedPath = window.location.pathname.substring(1);
  const allegedDomain = trimmedPath.substring(0, trimmedPath.indexOf("/"));
  if (allegedDomain === "") return undefined;
  return allegedDomain;
};

export const getDomainSettings = async (
  domainCode: string,
  user: IGetLandlordResponse | IGetTenantResponse
) => {
  const response = await get<IDomainSetting[]>(domainSettingsUrl(domainCode));
  const settings = formatDomainSettings(response, user);
  return settings;
};

export const formatDomainSettings = (
  domainSettings: IDomainSetting[],
  user: IGetLandlordResponse | IGetTenantResponse
) => {
  if (!canUserUseMobileApp(domainSettings)) {
    return null;
  }
  const currency = getCurrency(domainSettings);
  const timeZone = getTimeZone(domainSettings);
  const culture = getCulture(domainSettings);
  let tenantAccessStatus: ITenantAccessStatus;
  if (isLandlordTypeGuard(user)) {
    tenantAccessStatus = {
      canViewTemperature: true,
      canViewHours: true,
      canViewMeters: false,
      canViewBillingResults: false,
    };
  }
  tenantAccessStatus = {
    canViewTemperature: canUserViewTemperature(domainSettings),
    canViewHours: canUserViewTemperatureHours(domainSettings),
    canViewMeters: canUserViewMeters(domainSettings),
    canViewBillingResults: canUserViewBillingResults(domainSettings),
  };
  const settings: IDomainSettings = {
    TenantAccessStatus: tenantAccessStatus,
    Currency: currency,
    TimeZone: timeZone,
    DefaultCulture: culture,
  };
  return settings;
};

export const updateDomainSettings =
  (selectedDomain: IDomain) => async (dispatch: ({}) => void) => {
    const userInfoResponse = await get<
      IGetLandlordResponse | IGetTenantResponse
    >(userInfoUrl);
    const settings = await getDomainSettings(
      selectedDomain.Code,
      userInfoResponse
    );
    if (settings) dispatch(actionCreator.setDomainSettings(settings));
  };
